//==========================================
//⚠️ Keep index files under 100 lines!
//Split logic into submodules as needed.
//==========================================
import { configureStore } from "@reduxjs/toolkit";
// import { applyMiddleware, compose } from "redux";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware from "redux-saga";
import rootReducer from "./reducers";
import rootSaga from "./sagas";

import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import { v4 as uuidv4 } from "uuid";
import { isPolygonClosed } from "../utils";

const sagaMiddleware = createSagaMiddleware();
// const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const middleware = [sagaMiddleware];

// Define temporary states that should not be persisted
const TEMPORARY_EA_STATES = [
  // Blob URLs - become invalid after refresh
  "imageCache",
  "imageLoadingStates",
  // Temporary upload/delete tracking states
  "imageUploadStatus",
  "imageDeleteStatus",
  "uploadCount",
  // Temporary loading/error states
  "loading",
  "error",
  "deleteLoading",
  "deleteError",
  "createLoading",
  "createError",
  "createSuccess",
  "updating",
  "updateError",
  "updateSuccess",
  "submitting",
  "submitError",
  "submitSuccess",
  "associatedStreamsLoading",
  "associatedStreamsError"
];

// Define initial values for temporary states
const TEMPORARY_EA_STATE_DEFAULTS = {
  imageCache: {},
  imageLoadingStates: {},
  imageUploadStatus: {},
  imageDeleteStatus: {},
  uploadCount: 0,
  loading: false,
  error: null,
  deleteLoading: false,
  deleteError: null,
  createLoading: false,
  createError: null,
  createSuccess: false,
  updating: false,
  updateError: null,
  updateSuccess: false,
  submitting: false,
  submitError: null,
  submitSuccess: false,
  associatedStreamsLoading: false,
  associatedStreamsError: null
};

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  stateReconciler: autoMergeLevel2,
  transforms: [
    {
      in: (state, key) => {
        if (key === "enrolledAppearanceReducer") {
          // Exclude temporary states from persistence
          const persistedState = { ...state };
          TEMPORARY_EA_STATES.forEach((tempKey) => {
            delete persistedState[tempKey];
          });
          return persistedState;
        }
        return state;
      },
      out: (state, key) => {
        if (key === "enrolledAppearanceReducer") {
          // Reinitialize temporary states with defaults
          return {
            ...state,
            ...TEMPORARY_EA_STATE_DEFAULTS
          };
        }
        if (key === "assistantPrompts") {
          // Reset transient UI state on rehydration; keep recents and favorites
          return {
            ...state,
            isPanelOpen: false,
            pendingText: null,
            lastToggleAction: null,
            previousFavorites: [],
            patchStatus: { isLoading: false, hasError: false, errorMessage: "", statusCode: 0 },
          };
        }
        return state;
      }
    }
  ],
  blacklist: [
    "imagesReducer",
    "notificationBannerReducer",
    "calibrationBanner",
    "sessionReducer",
    "errorReducer",
    "cameraOnboardingReducer",
    "eventReducer",
    "polygonRoiReducer",
    "idleTimeModalReducer",
    "enrolledAppearanceReducer",
    "chatbot",
    // assistantPrompts: recents are session-only (cleared on logout, like sensor-insights-portal).
    // Favorites are persisted via the backend customdata API (fetched on mount).
    "assistantPrompts"
  ]
};
export { persistConfig };
const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware
});
sagaMiddleware.run(rootSaga);

export default store;

export const initStore = (initialPolygons) => {
  const filteredPolygons = initialPolygons?.length
    ? initialPolygons
        .filter((polygon) => isPolygonClosed(polygon.points))
        .map((polygon, index) => ({
          id: uuidv4(),
          label: `Polygon ${index + 1}`,
          isFinished: true,
          flattenedPoints: polygon.points.reduce((a, b) => a.concat(b), []),
          ...polygon
        }))
    : [];

  return configureStore({
    reducer: rootReducer,
    preloadedState: initialPolygons && {
      polygon: {
        past: [],
        present: {
          polygons: filteredPolygons,
          activePolygonIndex: filteredPolygons.length - 1
        },
        future: []
      }
    }
  });
};
